// src/admin/ManageMessages.js
import React, { useEffect, useState } from 'react';
import { db } from '../config/firebaseConfig';
import { collection, query, orderBy, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { Modal, Button } from 'react-bootstrap'; 

const ManageMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtreleme ve Pagination State'leri
  const [filterSubject, setFilterSubject] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Modal State'leri
  const [showModal, setShowModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const q = query(collection(db, "contactMessages"), orderBy("createdAt", "desc"));
        const querySnapshot = await getDocs(q);
        const msgs = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setMessages(msgs);
      } catch (error) {
        console.error("Error fetching messages:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this message? This action cannot be undone.")) {
      try {
        await deleteDoc(doc(db, "contactMessages", id));
        setMessages((prevMessages) => prevMessages.filter((msg) => msg.id !== id));
        if (selectedMessage?.id === id) {
            handleCloseModal();
        }
      } catch (error) {
        console.error("Error deleting message:", error);
        alert("Failed to delete message.");
      }
    }
  };

  const handleViewMessage = (msg) => {
    setSelectedMessage(msg);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setTimeout(() => setSelectedMessage(null), 200); 
  };

  const filteredMessages = messages.filter(msg => {
    if (filterSubject === 'All') return true;
    return msg.subject === filterSubject;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredMessages.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredMessages.length / itemsPerPage);

  const handleFilterChange = (e) => {
    setFilterSubject(e.target.value);
    setCurrentPage(1);
  };

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Rozet (Badge) rengini belirleyen yardımcı fonksiyon
  const getBadgeColor = (subject) => {
    switch(subject) {
      case 'Manuscript': return 'bg-primary';
      case 'Events': return 'bg-success';
      case 'Illustrator': return 'bg-info text-dark';
      case 'Workshop': return 'bg-warning text-dark';
      default: return 'bg-secondary';
    }
  };

  if (loading) return <div className="p-4">Loading messages...</div>;

  return (
    <div className="container-fluid p-4">
      
      {/* Başlık ve Filtre */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-4">
        <h2 className="mb-0">Contact Form Messages</h2>
        
        <div className="d-flex align-items-center gap-2 mt-3 mt-md-0">
          <label className="fw-bold">Filter by Subject:</label>
          <select 
            className="form-select w-auto" 
            value={filterSubject} 
            onChange={handleFilterChange}
          >
            <option value="All">All Subjects</option>
            <option value="Manuscript">Manuscript</option>
            <option value="Events">Events</option>
            <option value="Illustrator">Illustrator</option>
            <option value="Workshop">Workshop</option>
            <option value="Something Else">Something Else</option>
          </select>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0 align-middle">
              <thead className="bg-light">
                <tr>
                  <th scope="col" className="ps-4">Date</th>
                  <th scope="col">Name</th>
                  <th scope="col">Subject</th>
                  <th scope="col">Email / Phone</th>
                  <th scope="col">Message Preview</th>
                  <th scope="col" className="text-end pe-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.length === 0 ? (
                  <tr><td colSpan="6" className="text-center py-4">No messages found.</td></tr>
                ) : (
                  currentItems.map((msg) => (
                    <tr key={msg.id}>
                      <td className="ps-4 text-muted" style={{fontSize: '0.9rem'}}>
                        {msg.createdAt?.toDate().toLocaleDateString()} <br/>
                        {msg.createdAt?.toDate().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      </td>
                      <td className="fw-medium">{msg.name}</td>
                      <td>
                        <span className={`badge rounded-pill ${getBadgeColor(msg.subject)}`}>
                          {msg.subject}
                        </span>
                        {/* Tabloda workshop adını küçük olarak göster */}
                        {msg.subject === 'Workshop' && msg.selectedWorkshop && (
                          <small className="d-block text-muted mt-1 text-truncate" style={{maxWidth: '180px', fontSize: '0.75rem'}}>
                            {msg.selectedWorkshop}
                          </small>
                        )}
                      </td>
                      <td>
                        <div className="d-flex flex-column">
                          <a href={`mailto:${msg.email}`} className="text-decoration-none">{msg.email}</a>
                          <small className="text-muted">{msg.phone}</small>
                        </div>
                      </td>
                      <td>
                        <p className="mb-0 text-truncate" style={{maxWidth: '200px', cursor:'pointer'}} onClick={() => handleViewMessage(msg)}>
                          {msg.message}
                        </p>
                      </td>
                      <td className="text-end pe-4">
                        <div className="d-flex justify-content-end gap-2">
                            <button 
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => handleViewMessage(msg)}
                                title="View Full Message"
                            >
                                <i className="bi bi-eye"></i>
                            </button>
                            <button 
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => handleDelete(msg.id)}
                                title="Delete Message"
                            >
                                <i className="bi bi-trash"></i>
                            </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Pagination */}
        {filteredMessages.length > itemsPerPage && (
          <div className="card-footer bg-white d-flex justify-content-end py-3">
            <nav>
              <ul className="pagination mb-0">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => paginate(currentPage - 1)}>Previous</button>
                </li>
                {[...Array(totalPages)].map((_, index) => (
                  <li key={index} className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}>
                    <button className="page-link" onClick={() => paginate(index + 1)}>
                      {index + 1}
                    </button>
                  </li>
                ))}
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => paginate(currentPage + 1)}>Next</button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </div>

      {/* MESSAGE DETAIL MODAL */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Message Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedMessage && (
            <div className="container-fluid">
              <div className="row mb-3 border-bottom pb-3">
                 <div className="col-md-6">
                    <small className="text-muted d-block">From</small>
                    <h5 className="mb-0">{selectedMessage.name}</h5>
                    <a href={`mailto:${selectedMessage.email}`} className="text-decoration-none">{selectedMessage.email}</a>
                    {selectedMessage.phone && <div className="text-muted small">{selectedMessage.phone}</div>}
                 </div>
                 <div className="col-md-6 text-md-end mt-3 mt-md-0">
                    <small className="text-muted d-block">Date</small>
                    <strong>{selectedMessage.createdAt?.toDate().toLocaleDateString()}</strong>
                    <div className="small text-muted">{selectedMessage.createdAt?.toDate().toLocaleTimeString()}</div>
                    <div className="mt-2">
                        <span className={`badge ${getBadgeColor(selectedMessage.subject)}`}>
                            {selectedMessage.subject}
                        </span>
                    </div>
                 </div>
              </div>
              
              {/* MODAL İÇİNDE WORKSHOP GÖSTERİMİ */}
              {selectedMessage.subject === 'Workshop' && selectedMessage.selectedWorkshop && (
                <div className="row mb-3">
                  <div className="col-12">
                    <div className="p-3 bg-warning-subtle rounded border border-warning">
                      <small className="text-dark fw-bold d-block mb-1">Selected Workshop:</small>
                      <span className="text-dark fs-6">{selectedMessage.selectedWorkshop}</span>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="row">
                <div className="col-12">
                    <small className="text-muted d-block mb-2">Message Content</small>
                    <div className="p-3 bg-light rounded border">
                        <p className="mb-0" style={{whiteSpace: 'pre-wrap'}}>{selectedMessage.message}</p>
                    </div>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={() => handleDelete(selectedMessage?.id)}>
            <i className="bi bi-trash me-2"></i>Delete Message
          </Button>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

    </div>
  );
};

export default ManageMessages;